import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchAllRootCausesByClaim, upsertRootCauseForClaim } from "../api/root-causes";
import { createSupplierPortalClient } from "../api/supplier-portal";
import { Button } from "@/shared/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { FiveWhyAnalysis } from "./FiveWhyAnalysis";
import { IshikawaDiagram } from "./IshikawaDiagram";
import { Save, Loader2, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/ui/tooltip";

interface RootCauseAnalysisProps {
  claimId: string;
  supplierToken?: string;
}

interface FiveWhyData {
  problem: string;
  whys: string[];
  rootCause: string;
}

interface IshikawaData {
  problem: string;
  categories: Array<{
    id: string;
    name: string;
    causes: string[];
  }>;
}

const defaultFiveWhyData: FiveWhyData = {
  problem: "",
  whys: ["", "", "", "", ""],
  rootCause: "",
};

const defaultIshikawaData: IshikawaData = {
  problem: "",
  categories: [],
};

export function RootCauseAnalysis({ claimId, supplierToken }: RootCauseAnalysisProps) {
  const queryClient = useQueryClient();
  const [activeMethod, setActiveMethod] = useState<"five_why" | "ishikawa">("five_why");
  const [fiveWhyData, setFiveWhyData] = useState<FiveWhyData>(defaultFiveWhyData);
  const [ishikawaData, setIshikawaData] = useState<IshikawaData>(defaultIshikawaData);
  const [hasChanges, setHasChanges] = useState(false);
  const supplierClient = supplierToken ? createSupplierPortalClient(supplierToken) : null;

  // Fetch existing data
  const { data: savedData, isLoading } = useQuery({
    queryKey: ["mod_8d_root_causes", claimId],
    queryFn: async () => {
      if (supplierClient) {
        const data = await supplierClient.fetchRootCauses();
        return Array.isArray(data) ? data : data ? [data] : [];
      }
      return fetchAllRootCausesByClaim(claimId);
    },
  });

  // Load saved data
  useEffect(() => {
    if (savedData) {
      const fiveWhy = savedData.find((d) => d.method === "five_why");
      const ishikawa = savedData.find((d) => d.method === "ishikawa");

      if (fiveWhy?.data) {
        setFiveWhyData(fiveWhy.data as unknown as FiveWhyData);
      }
      if (ishikawa?.data) {
        setIshikawaData(ishikawa.data as unknown as IshikawaData);
      }
    }
  }, [savedData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const dataToSave = activeMethod === "five_why" ? fiveWhyData : ishikawaData;
      if (supplierClient) {
        await supplierClient.upsertRootCause(activeMethod, JSON.parse(JSON.stringify(dataToSave)));
        return;
      }
      await upsertRootCauseForClaim(claimId, activeMethod, JSON.parse(JSON.stringify(dataToSave)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mod_8d_root_causes", claimId] });
      setHasChanges(false);
      toast.success("Analysis saved");
    },
    onError: () => toast.error("Failed to save analysis"),
  });

  const handleFiveWhyChange = (data: FiveWhyData) => {
    setFiveWhyData(data);
    setHasChanges(true);
  };

  const handleIshikawaChange = (data: IshikawaData) => {
    setIshikawaData(data);
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs value={activeMethod} onValueChange={(v) => setActiveMethod(v as "five_why" | "ishikawa")}>
        <div className="flex items-center justify-between gap-4">
          <TabsList className="grid w-full max-w-xs grid-cols-2">
            <TabsTrigger value="five_why" className="text-xs">
              5-Why Analysis
            </TabsTrigger>
            <TabsTrigger value="ishikawa" className="text-xs">
              Ishikawa (6M)
            </TabsTrigger>
          </TabsList>
          
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs">
                {activeMethod === "five_why" ? (
                  <p className="text-xs">
                    <strong>5-Why Analysis:</strong> Keep asking "Why?" to drill down from the symptom to the root cause. 
                    Typically 5 iterations are needed, but you can add more or fewer as needed.
                  </p>
                ) : (
                  <p className="text-xs">
                    <strong>Ishikawa (Fishbone) Diagram:</strong> Organize potential causes into 6 categories: 
                    Man, Machine, Method, Material, Measurement, and Environment. 
                    Click each category to add specific causes.
                  </p>
                )}
              </TooltipContent>
            </Tooltip>
            
            <Button
              size="sm"
              onClick={() => saveMutation.mutate()}
              disabled={!hasChanges || saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>

        <TabsContent value="five_why" className="mt-4">
          <FiveWhyAnalysis data={fiveWhyData} onChange={handleFiveWhyChange} />
        </TabsContent>

        <TabsContent value="ishikawa" className="mt-4">
          <IshikawaDiagram data={ishikawaData} onChange={handleIshikawaChange} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
